import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";
import {
  affichageDeLecture,
  ErreurColoriage,
  FilColoriage,
  IntrouvableColoriage,
  idValide,
  LIEN_BOUTON_PLEIN,
  RelectureEchouee,
} from "./coloriage-commun";
// Le clavier de l'ecran 3, tel quel : memes touches, memes garde-fous (champ de saisie,
// Ctrl/Meta/Alt, touche maintenue). Recopier ces regles serait les laisser deriver.
import { actionDeTouche } from "./coloriage-page";
import {
  messageDEchec,
  useComparaison,
  useJugerAveugle,
  useProchaineAveugle,
} from "./coloriage-requetes";
import {
  avancementDuRendu,
  JUGEMENTS,
  LIBELLE_JUGEMENT,
  passageEnClair,
  RENDU_AVANT_JUGEMENT,
  TOUCHE_JUGEMENT,
  titreComparaison,
  titrePage,
} from "./coloriage-textes";
import type { Jugement, ProchaineAveugle, VersionAveugle } from "./contract";
import { ConsoleShell } from "./shell";
import { Chargement } from "./states";

/**
 * Juger a l'aveugle (`/coloriage/comparaisons/$id/juger`), le mode cache de l'ecran 3. Textes
 * et disposition : `ColoriageAveugleMobile.dc.html`, une maquette de telephone (390 px) :
 * une colonne, l'image sur toute la largeur, la barre des trois boutons collee en bas.
 *
 * Trois regles tiennent l'ecran :
 *
 * - **Rien de la variante n'entre dans le DOM.** Le contrat n'envoie ni prompt, ni graine, ni
 *   nom de variante, ni numero d'essai (`versionAveugleSchema`) ; cet ecran n'en deduit rien
 *   non plus : il n'affiche que ce que la reponse porte, l'ordre lui vient du backend (melange
 *   mais stable), et la cle React d'une version est son identifiant, jamais sa colonne. Il ne
 *   lit de la comparaison que sa date, pour le fil : son tableau, s'il etait la, ne serait
 *   jamais rendu.
 * - **La suivante arrive sans changer d'adresse.** La reponse d'un jugement porte la version
 *   suivante, posee dans le cache par `useJugerAveugle` ; a la fin, retour a la comparaison.
 * - **Le clavier est celui de l'ecran 3** : 1, 2, 3, par `actionDeTouche`. Les autres actions
 *   de cette fonction (choisir, changer de version) n'existent pas ici : on ne choisit pas a
 *   l'aveugle, et l'ordre n'est pas le notre.
 */

export function ColoriageAveuglePage({ id }: { id: string }) {
  const valide = idValide(id);
  const lecture = useProchaineAveugle(id, valide);
  const comparaison = useComparaison(id, valide);
  // Jamais l'ecran d'erreur et la version ensemble (`affichageDeLecture`).
  const affichage = affichageDeLecture(lecture);
  const prochaine = affichage.contenu ? lecture.data : undefined;

  return (
    <ConsoleShell connecte={affichage.contenu}>
      <FilColoriage>
        <Link className="underline-offset-2 hover:underline" to="/coloriage/variantes">
          Variantes
        </Link>
        <span aria-hidden="true"> › </span>
        {valide ? (
          <Link
            className="underline-offset-2 hover:underline"
            params={{ id }}
            to="/coloriage/comparaisons/$id"
          >
            {comparaison.data === undefined ? "Comparaison" : titreComparaison(comparaison.data)}
          </Link>
        ) : (
          "Comparaison"
        )}
        <span aria-hidden="true"> › </span>À l'aveugle
      </FilColoriage>
      {!valide ? <IntrouvableColoriage quoi={`la comparaison ${id}`} /> : null}
      {valide && lecture.isPending ? <Chargement quoi="de la prochaine version" /> : null}
      {affichage.ecranDErreur ? (
        <ErreurColoriage error={lecture.error} quoi={`la comparaison ${id}`} />
      ) : null}
      {affichage.bandeau ? <RelectureEchouee error={lecture.error} /> : null}
      {prochaine === undefined ? null : <Aveugle comparaisonId={id} prochaine={prochaine} />}
    </ConsoleShell>
  );
}

function Aveugle({
  comparaisonId,
  prochaine,
}: {
  comparaisonId: string;
  prochaine: ProchaineAveugle;
}) {
  const navigate = useNavigate();
  const juger = useJugerAveugle(comparaisonId);
  const [note, setNote] = useState("");
  const [echec, setEchec] = useState<string | null>(null);
  const version = prochaine.prochaine;

  const jugerCourante = (jugement: Jugement): void => {
    if (version === null || juger.isPending) return;
    setEchec(null);
    const propre = note.trim();
    juger.mutate(
      { version: version.version, jugement, note: propre === "" ? null : propre },
      {
        onSuccess: (reponse) => {
          setNote("");
          // Plus rien a juger : le tableau sort, on y va.
          if (reponse.prochaine === null) {
            void navigate({
              to: "/coloriage/comparaisons/$id",
              params: { id: comparaisonId },
            });
          }
        },
        onError: (error) => setEchec(messageDEchec(error)),
      },
    );
  };

  // Le clavier : un seul ecouteur sur la fenetre, qui lit toujours l'etat du dernier rendu.
  const clavier = useRef<(event: KeyboardEvent) => void>(() => undefined);
  useEffect(() => {
    clavier.current = (event) => {
      const action = actionDeTouche(event);
      // A l'aveugle, seules les trois touches de jugement font quelque chose : on ne choisit
      // pas, et les fleches ne changent pas de version puisque l'ordre n'est pas le notre.
      if (action === null || action.type !== "juger") return;
      event.preventDefault();
      jugerCourante(action.jugement);
    };
  });
  useEffect(() => {
    const ecouter = (event: KeyboardEvent): void => clavier.current(event);
    window.addEventListener("keydown", ecouter);
    return () => window.removeEventListener("keydown", ecouter);
  }, []);

  // Rien a juger, pour deux raisons opposees : le rendu n'est pas fini (la route refuse de
  // servir avant, pour ne pas faire juger une serie entiere issue d'une meme variante), ou tout
  // est juge. L'ecran ne doit pas annoncer que tout est juge dans le premier cas.
  if (version === null) {
    const rendu = prochaine.arrivees >= prochaine.total;
    return (
      <>
        <header>
          <h1 className="text-2xl font-semibold">Juger à l'aveugle</h1>
          <p className="mt-1 max-w-[80ch] text-sm text-muted-foreground">
            {rendu
              ? `Les ${prochaine.total} versions de cette comparaison sont jugées : les noms des variantes sont revenus.`
              : `${avancementDuRendu(prochaine)} ${RENDU_AVANT_JUGEMENT}`}
          </p>
        </header>
        <div>
          <Link
            className={LIEN_BOUTON_PLEIN}
            params={{ id: comparaisonId }}
            to="/coloriage/comparaisons/$id"
          >
            {rendu ? "Voir le tableau" : "Revenir à la comparaison"}
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <header>
        <h1 className="text-xl font-semibold">Juger à l'aveugle</h1>
        <p className="mt-1 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span>
            {titrePage(version.rang, version.titre)} ·{" "}
            {passageEnClair(version.passage, prochaine.total)}
          </span>
          <span className="inline-flex whitespace-nowrap rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
            variante cachée
          </span>
        </p>
      </header>

      {/* Une colonne, l'image sur toute la largeur : la maquette est une maquette de telephone,
          et sur grand ecran la colonne reste bornee pour que l'image ne parte pas en geant. */}
      <div className="flex max-w-[40rem] flex-col gap-4">
        <section
          aria-label="Version à juger"
          className="min-w-0 rounded-lg border border-border bg-card p-2.5 text-card-foreground"
        >
          {/* La cle est ici, pas sur le `img` a l'interieur : c'est ce composant qui porte
              l'etat « image cassee », et sans remontage il restait cassee pour toutes les
              versions suivantes. Elle vaut l'identifiant de la version, jamais son rang de
              passage ni sa colonne. */}
          <ImageAveugle key={version.version} version={version} />
          <p className="px-1.5 pt-2 text-xs text-muted-foreground">
            Pince pour zoomer · les versions arrivent mélangées
          </p>
        </section>

        <Signalements version={version} />

        <textarea
          aria-label="Note"
          className="min-h-16 w-full rounded-md border border-input bg-background px-2.5 py-2 text-sm leading-normal placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          maxLength={500}
          onChange={(event) => setNote(event.target.value)}
          onKeyDown={(event) => {
            // Echap rend la main au clavier de la page, sans passer par la souris.
            if (event.key === "Escape") event.currentTarget.blur();
          }}
          placeholder="Une note, si besoin"
          rows={2}
          value={note}
        />

        {/* La barre des boutons, collee en bas de l'ecran sous `md` comme sur l'ecran 3. La
            marge basse qu'elle impose au corps est dans `theme.css` (`coloriage-barre`). */}
        <div className="coloriage-barre flex flex-col gap-2.5 max-md:fixed max-md:inset-x-0 max-md:bottom-0 max-md:z-30 max-md:border-t max-md:border-border max-md:bg-card max-md:px-4 max-md:pt-3 max-md:pb-4 max-md:shadow-[0_-4px_12px_rgb(0_0_0/0.08)]">
          <div className="grid grid-cols-3 gap-2.5">
            {JUGEMENTS.map((valeur) => (
              <button
                aria-keyshortcuts={TOUCHE_JUGEMENT[valeur]}
                className={cn(
                  "flex items-center justify-center gap-2 rounded-lg border-[1.5px] px-1 py-3.5 text-sm font-semibold transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                  "disabled:cursor-not-allowed disabled:opacity-50",
                  STYLE_JUGEMENT[valeur],
                )}
                disabled={juger.isPending}
                key={valeur}
                onClick={() => jugerCourante(valeur)}
                type="button"
              >
                {LIBELLE_JUGEMENT[valeur]}
                <Touche>{TOUCHE_JUGEMENT[valeur]}</Touche>
              </button>
            ))}
          </div>
          <p className="text-center text-xs text-muted-foreground">
            Ni prompt, ni graine, ni nom de variante : ils reviennent quand les {prochaine.total}{" "}
            sont jugées.
          </p>
          {echec === null ? null : (
            <p className="text-sm text-destructive" role="alert">
              {echec}
            </p>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * L'image d'une version a juger a l'aveugle. Son adresse vient du backend telle quelle :
 * l'ecran n'a pas l'empreinte de la version, et n'a donc rien a fabriquer (`ImageVersion` de
 * `coloriage-commun` compose l'adresse a partir de l'empreinte, qui ne sort pas ici). Les
 * dimensions sont posees sur la balise : la page ne saute pas quand les octets arrivent.
 */
function ImageAveugle({ version }: { version: VersionAveugle }) {
  const [cassee, setCassee] = useState(false);
  if (cassee) {
    return (
      <span
        className="flex w-full items-center justify-center rounded-md border border-dashed border-border bg-muted p-2 text-center text-xs text-muted-foreground"
        style={{ aspectRatio: `${version.largeur} / ${version.hauteur}` }}
      >
        image pas encore arrivée
      </span>
    );
  }
  return (
    <img
      // Le rang de passage nomme l'image : il ne dit ni la variante, ni le numero de la version.
      alt={`${titrePage(version.rang, version.titre)}, version ${version.passage}`}
      className="block h-auto w-full rounded-md border border-border bg-white"
      height={version.hauteur}
      onError={() => setCassee(true)}
      src={version.image}
      width={version.largeur}
    />
  );
}

/** Les signalements du PC, les erreurs d'abord, comme sur l'ecran 3. */
function Signalements({ version }: { version: VersionAveugle }) {
  const signalements = [
    ...version.controle.signalements.filter((une) => une.gravite === "erreur"),
    ...version.controle.signalements.filter((une) => une.gravite !== "erreur"),
  ];
  if (signalements.length === 0) return null;
  return (
    <ul aria-label="Contrôles automatiques" className="flex flex-col gap-2">
      {signalements.map((signalement, rang) => (
        <li
          className={cn(
            "rounded-md border px-3.5 py-2.5 text-sm",
            signalement.gravite === "erreur"
              ? "border-jugement-jeter/40 bg-jugement-jeter/15 text-jugement-jeter"
              : "border-jugement-defaut/40 bg-jugement-defaut/15 text-jugement-defaut",
          )}
          // Deux signalements du meme code sont possibles : le rang les distingue.
          // biome-ignore lint/suspicious/noArrayIndexKey: liste figee, jamais reordonnee
          key={`${signalement.code}-${rang}`}
        >
          <span className="font-semibold">
            {signalement.gravite === "erreur" ? "Erreur" : "Avertissement"} :
          </span>{" "}
          {signalement.message}
        </li>
      ))}
    </ul>
  );
}

/** Le bouton libre de chaque jugement : a l'aveugle, aucun n'est jamais presse. */
const STYLE_JUGEMENT: Readonly<Record<Jugement, string>> = {
  bonne: "border-jugement-bonne/50 bg-card text-jugement-bonne enabled:hover:bg-jugement-bonne/15",
  defaut:
    "border-jugement-defaut/50 bg-card text-jugement-defaut enabled:hover:bg-jugement-defaut/15",
  a_jeter:
    "border-jugement-jeter/50 bg-card text-jugement-jeter enabled:hover:bg-jugement-jeter/15",
};

/**
 * Une touche du clavier, dessinee. Muette : le bouton porte `aria-keyshortcuts`. Un `span` et
 * non un `kbd`, que Biome prend pour un element focalisable des qu'il est masque.
 */
function Touche({ children }: { children: string }) {
  return (
    <span
      aria-hidden="true"
      className="rounded border border-border border-b-2 bg-muted px-1.5 font-mono text-xs font-medium text-muted-foreground max-md:hidden"
    >
      {children}
    </span>
  );
}
